import Button from '@components/Button';
import { Collapse } from '@components/Collapse';
import Image from '@components/Image';
import ImageContainer from '@components/ImageContainer';
import Slider from '@components/Slider';
import Textarea from '@components/Textarea';
import Tip from '@components/Tip';
import { Validator } from '@components/Validator';
import useTextareaHandelrs from '@hooks/useTextareaHandelrs';
import { selectLibSandtableMarkerImages } from '@modules/mediaLib';
import { $editingMapMarkerApi } from '@stores/editingMarkerStores';
import {
  ImageType,
  type STMapMarker,
  type STMapMarkerImage,
  STMapMarkerType,
} from '@types';
import getImageSize from '@utils/getImageSize';
import { getMediaUrl, getSysUrl } from '@utils/urlMakers';
import type { Draft } from 'immer';
import { toPercent } from 'pano-utils';
import { useEffect, useState } from 'react';
import { stEditorAmap } from '../STAMap';

interface Props {
  marker: STMapMarkerImage;
}

const valToString = (val: number) => toPercent(val, 0);

export function FeatureSettingsImage({ marker }: Props) {
  const updateMarkerObject = (markerData: STMapMarkerImage) => {
    const markerObject = stEditorAmap.getMarkerObjectById(
      marker.id
    ) as AMap.Marker | null;
    if (!markerObject) return;

    markerObject.setExtData({
      ...markerObject.getExtData(),
      markerData,
    });
    stEditorAmap
      .generateImageContent(markerData.icon, { isSelected: true })
      .then((iconObj) => {
        markerObject.setIcon(iconObj);
      });
  };
  const [title, setTitle] = useState(marker.title);

  useEffect(() => {
    updateMarkerObject(marker);
  }, [marker]);

  const updateMarker = (
    updater: (draft: Draft<STMapMarkerImage>) => STMapMarker | void
  ) => {
    $editingMapMarkerApi.update((draft) => {
      draft.type ??= STMapMarkerType.image;
      if (draft.type !== STMapMarkerType.image) return;
      updater(draft);
    });
  };

  const handlers = useTextareaHandelrs('trimRight', (val) => {
    updateMarker((draft) => {
      draft.title = val;
    });
  });

  const handleImageSelect = async () => {
    const { libItems, sysItems } = await selectLibSandtableMarkerImages();
    if (libItems) {
      const size = await getImageSize(getMediaUrl(libItems[0].url));
      if (size) {
        updateMarker((draft) => {
          draft.icon.image = {
            type: 1,
            id: libItems[0].id,
            url: libItems[0].url,
          };
        });
      }
    } else if (sysItems) {
      const size = await getImageSize(getSysUrl(sysItems[0].url));
      if (size) {
        updateMarker((draft) => {
          draft.icon.image = {
            type: 0,
            id: 0,
            url: sysItems[0].url,
          };
        });
      }
    }
  };

  return (
    <>
      <Collapse title="贴图设置">
        <Validator
          value={marker.icon.image.url}
          validFn={(val) => !!val}
          warningMessage="请添加一张图片"
        >
          {({ value, warning }) => (
            <ImageContainer
              warning={warning}
              className="mt-10 h-115"
              placeholder={
                <div className="flex flex-col items-center">
                  <p>图片长/宽不超过2000PX</p>
                  <p>支持JPG/PNG/GIF</p>
                </div>
              }
            >
              {!!value && (
                <Image
                  src={value}
                  type={
                    marker.icon.image.type === 0
                      ? ImageType.sysMedia
                      : ImageType.media
                  }
                  className="max-h-full max-w-full"
                />
              )}
            </ImageContainer>
          )}
        </Validator>
        <div className="mt-10 flex items-center justify-between">
          <Tip>建议大小：不超过3MB</Tip>
          <Button size="sm" onClick={handleImageSelect}>
            选择图片
          </Button>
        </div>
        {!!marker.icon.image.url && (
          <Slider
            className="mt-20"
            min={0.1}
            max={2}
            value={marker.icon.scaleX}
            valToString={valToString}
            onChange={(val) => {
              updateMarker((draft) => {
                draft.icon.scaleX = val;
                draft.icon.scaleY = val;
              });
            }}
          >
            <Slider.Header title="图标缩放：" />
            <Slider.Bar step={0.01} />
            <Slider.Control step={0.01} />
          </Slider>
        )}
      </Collapse>
      <Collapse title="标记标题">
        <Textarea
          value={title}
          scheme="dark"
          className="mb-20 h-115"
          placeholder="请输入标记标题"
          maxLength={500}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={handlers.onBlur}
        />
      </Collapse>
    </>
  );
}
